import { Chip, Tooltip, Typography } from "@material-ui/core";
import Divider from "@material-ui/core/Divider";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import StarIcon from "@material-ui/icons/Star";
import CustomCardMenu from "@saleor/components/CustomCardMenu";
import Skeleton from "@saleor/components/Skeleton";
import { Coupons_coupons_edges_node } from "@saleor/coupons/types/Coupons";
import { couponPath } from "@saleor/coupons/url";
import { CouponReorderActionEnum } from "@saleor/types/globalTypes";
import React, { useEffect, useState } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult
} from "react-beautiful-dnd";
import { useIntl } from "react-intl";

import couponRankingCalendarIcon from "../../../../assets/images/coupon-calendar.svg";
import couponRankingTicketIcon from "../../../../assets/images/coupon-ticket.svg";
import dragIndicator from "../../../../assets/images/drag-indicator.svg";
import useCouponRankingStyle, {
  CouponStatus,
  fiberIconColors,
  getListStyle,
  statusColors
} from "./style";

export interface ReorderDetails {
  newOrder: string[];
  movedCouponId: string;
  beforeItemId: string | null;
  afterItemId: string | null;
}

export interface PresetReorderDetails {
  couponId: string;
  reorderPreset: CouponReorderActionEnum;
}

interface CouponRankingListProps {
  coupons?: Coupons_coupons_edges_node[];
  onReorder: (reorderDetails: ReorderDetails) => void;
  onPresetReorder: (presetReorderDetails: PresetReorderDetails) => void;
  lastMovedCouponId?: string | null;
}

const CouponRankingList: React.FC<CouponRankingListProps> = ({
  coupons,
  onReorder,
  onPresetReorder,
  lastMovedCouponId
}) => {
  const classes = useCouponRankingStyle({});

  const [listItems, setListItems] = useState<Coupons_coupons_edges_node[]>([]);
  const [originalOrder, setOriginalOrder] = useState<string[]>([]);
  const couponDealType = coupons?.[0]?.dealType;
  const [isDragging, setIsDragging] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const clearSelection = () => setSelectedCardId(null);

  const toggleHighlight = (couponId: string) => {
    setSelectedCardId(prevId => (prevId === couponId ? null : couponId));
  };

  useEffect(() => {
    setListItems(coupons || []);
    const initialOrder = coupons?.map(coupon => coupon.id) || [];
    setOriginalOrder(initialOrder);
  }, [coupons]);

  const intl = useIntl();

  const formatDate = dateString => {
    const date = new Date(dateString);
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December"
    ];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();

    return `${month} ${day}, ${year}`;
  };

  useEffect(() => {
    setListItems(coupons);
  }, [coupons]);
  const [shadowIndex, setShadowIndex] = useState<number | null>(null);
  const [moveDirection, setMoveDirection] = useState(null);

  const onDragStart = () => {
    setIsDragging(true);
  };

  const onDragUpdate = update => {
    if (!update.destination) {
      return;
    }

    const sourceIndex = update.source.index;
    const destinationIndex = update.destination.index;

    const direction = destinationIndex > sourceIndex ? "down" : "up";
    setMoveDirection(direction);

    setShadowIndex(update.destination.index);
  };

  const onDragEnd = (result: DropResult) => {
    setIsDragging(false);
    if (
      !result.destination ||
      result.destination.index === result.source.index
    ) {
      return;
    }

    const newOrderListItems = Array.from(listItems);
    const [reorderedItem] = newOrderListItems.splice(result.source.index, 1);

    newOrderListItems.splice(result.destination.index, 0, reorderedItem);

    const newOrderIds = newOrderListItems.map(coupon => coupon.id);
    const hasOrderChanged = originalOrder.some(
      (id, index) => id !== newOrderIds[index]
    );

    if (hasOrderChanged) {
      const beforeItemId =
        result.destination.index > 0
          ? newOrderIds[result.destination.index - 1]
          : null;
      const afterItemId =
        result.destination.index < newOrderIds.length - 1
          ? newOrderIds[result.destination.index + 1]
          : null;

      const reorderDetails: ReorderDetails = {
        afterItemId,
        beforeItemId,
        movedCouponId: reorderedItem.id,
        newOrder: newOrderIds
      };

      onReorder(reorderDetails);
    }

    setShadowIndex(null);
    setListItems(newOrderListItems);
  };

  const handleMouseEnter = couponId => {
    if (couponId !== selectedCardId) {
      clearSelection();
    }
  };

  return (
    <>
      <DragDropContext
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragUpdate={onDragUpdate}
      >
        <Droppable droppableId="droppable" id="couponDroppableCard">
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={getListStyle()}
            >
              {listItems
                .filter(
                  coupon =>
                    coupon.status === "ACTIVE" ||
                    coupon.status === "INACTIVE" ||
                    coupon.status === "SCHEDULED" ||
                    coupon.status === "COMPLETED"
                )
                .map((coupon, index) => (
                  <React.Fragment key={coupon.id}>
                    {snapshot.isDraggingOver &&
                      moveDirection === "up" &&
                      shadowIndex === index && (
                        <div
                          className={`${classes.dashedPlaceholder} ${
                            isDragging ? classes.showPlaceholder : ""
                          }`}
                        ></div>
                      )}
                    <Draggable draggableId={coupon.id} index={index}>
                      {provided => (
                        <>
                          <div
                            onMouseEnter={() => handleMouseEnter(coupon.id)}
                            onMouseLeave={clearSelection}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`${classes.flexRow}`}
                          >
                            <div
                              className={`${classes.draggableCard} ${
                                selectedCardId === coupon.id ? "selected" : ""
                              }  ${
                                lastMovedCouponId === coupon.id
                                  ? classes.movedCard
                                  : classes.draggableCard
                              }`}
                            >
                              <div className={classes.flexFullWidth}>
                                <div className={classes.halfWidthContainer}>
                                  <img
                                    src={dragIndicator}
                                    alt="dragAnchor"
                                    id="couponAnchor"
                                    {...provided.dragHandleProps}
                                  />
                                </div>
                                <div className={classes.halfWidthFlexContainer}>
                                  {coupon && coupon.name ? (
                                    <Tooltip title={coupon.name}>
                                      <Typography
                                        className={classes.couponTitle}
                                      >
                                        {coupon.name.length > 15
                                          ? `${coupon.name.substring(0, 15)}...`
                                          : coupon.name}
                                      </Typography>
                                    </Tooltip>
                                  ) : (
                                    <Skeleton />
                                  )}
                                </div>
                                <div className={classes.halfWidthFlexContainer}>
                                  {coupon && coupon.status ? (
                                    <Chip
                                      className={classes.chip}
                                      label={
                                        <Typography
                                          className={classes.couponStatus}
                                        >
                                          {CouponStatus[coupon.status]}
                                        </Typography>
                                      }
                                      style={{
                                        backgroundColor:
                                          statusColors[coupon.status]
                                      }}
                                      icon={
                                        <FiberManualRecordIcon
                                          style={{
                                            color:
                                              fiberIconColors[coupon.status]
                                          }}
                                        />
                                      }
                                    />
                                  ) : (
                                    <Skeleton />
                                  )}
                                </div>
                                <div className={classes.halfWidthFlexContainer}>
                                  {coupon && coupon.code ? (
                                    <Typography className={classes.couponCode}>
                                      {coupon.code}
                                    </Typography>
                                  ) : (
                                    <Skeleton />
                                  )}
                                </div>
                                <div className={classes.halfWidthFlexContainer}>
                                  {couponDealType === "DEALS" ? (
                                    <>
                                      <div>
                                        <img
                                          src={couponRankingCalendarIcon}
                                          alt="rankingCouponCalendarIcon"
                                          className={classes.calendarIcon}
                                        />
                                      </div>
                                      <div
                                        className={classes.flexGrowContainer}
                                      >
                                        <Typography
                                          className={classes.textLabel}
                                        >
                                          Claiming Period:
                                        </Typography>
                                        <Typography
                                          className={classes.claimingDates}
                                        >
                                          {formatDate(coupon.startDate)} -{" "}
                                          {formatDate(coupon.endDate)}
                                        </Typography>
                                      </div>
                                    </>
                                  ) : couponDealType === "EVENTS" ? (
                                    <>
                                      <img
                                        src={couponRankingCalendarIcon}
                                        alt="rankingCouponCalendarIcon"
                                        className={classes.calendarIcon}
                                      />
                                      <div
                                        className={classes.flexGrowContainer}
                                      >
                                        <Typography
                                          className={classes.textLabel}
                                        >
                                          Event Date:
                                        </Typography>
                                        <Typography
                                          className={classes.claimingDates}
                                        >
                                          {formatDate(coupon.eventEndDate)}
                                        </Typography>
                                      </div>
                                    </>
                                  ) : null}
                                  <Divider orientation="vertical" />
                                </div>
                                <div className={classes.halfWidthFlexContainer}>
                                  <img
                                    src={couponRankingTicketIcon}
                                    alt="rankingCouponTicketIcon"
                                    className={classes.ticketIcon}
                                  />
                                  <div className={classes.flexGrowContainer}>
                                    <Typography className={classes.textLabel}>
                                      Coupons Remaining:
                                    </Typography>
                                    <Typography
                                      className={classes.remainingQuantities}
                                    >
                                      {coupon.availableQuantity}
                                    </Typography>
                                  </div>
                                </div>
                                <div className={classes.halfWidthFlexContainer}>
                                  <StarIcon className={classes.starIcon} />
                                </div>
                              </div>
                            </div>
                            <div
                              className={classes.cardMenuContainer}
                              onClick={() => toggleHighlight(coupon.id)}
                            >
                              <CustomCardMenu
                                disabled={false}
                                onClose={clearSelection}
                                menuItems={[
                                  {
                                    label: (
                                      <>
                                        {intl.formatMessage({
                                          defaultMessage:
                                            "View Coupon in new Tab",
                                          description: "button"
                                        })}
                                        <OpenInNewIcon
                                          className={classes.couponCardMenu}
                                        />
                                      </>
                                    ),
                                    onSelect: () => {
                                      window.open(couponPath(coupon.id));
                                    }
                                  },
                                  {
                                    isStaticText: true,
                                    label: intl.formatMessage({
                                      defaultMessage: "MOVE TO",
                                      description: "sectionTitle"
                                    })
                                  },
                                  {
                                    isDivider: true
                                  },
                                  ...(index > 0
                                    ? [
                                        {
                                          label: intl.formatMessage({
                                            defaultMessage: "Top Most",
                                            description: "button"
                                          }),
                                          onSelect: () =>
                                            onPresetReorder({
                                              couponId: coupon.id,
                                              reorderPreset:
                                                CouponReorderActionEnum.TOP_MOST
                                            })
                                        },
                                        {
                                          label: intl.formatMessage({
                                            defaultMessage: "Top",
                                            description: "button"
                                          }),
                                          onSelect: () =>
                                            onPresetReorder({
                                              couponId: coupon.id,
                                              reorderPreset:
                                                CouponReorderActionEnum.ONE_UP
                                            })
                                        }
                                      ]
                                    : []),
                                  ...(index < listItems.length - 1
                                    ? [
                                        {
                                          label: intl.formatMessage({
                                            defaultMessage: "Bottom",
                                            description: "button"
                                          }),
                                          onSelect: () =>
                                            onPresetReorder({
                                              couponId: coupon.id,
                                              reorderPreset:
                                                CouponReorderActionEnum.ONE_DOWN
                                            })
                                        },
                                        {
                                          label: intl.formatMessage({
                                            defaultMessage: "Bottom Most",
                                            description: "button"
                                          }),
                                          onSelect: () =>
                                            onPresetReorder({
                                              couponId: coupon.id,
                                              reorderPreset:
                                                CouponReorderActionEnum.BOTTOM_MOST
                                            })
                                        }
                                      ]
                                    : [])
                                ]}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </Draggable>
                    {snapshot.isDraggingOver &&
                      moveDirection === "down" &&
                      shadowIndex === index && (
                        <div className={classes.dashedPlaceholder}></div>
                      )}
                  </React.Fragment>
                ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <p className={classes.footer}>
        <Divider variant="middle" />
        End of Featured Coupons
        <Divider variant="middle" />
      </p>
    </>
  );
};

export default CouponRankingList;
